/**
 * Metin normalizasyonu ve offset eşlemesi.
 *
 * PDF'ten çıkan metin güvenilmez: bitişik harfler, yumuşak tireler, operatör
 * kaybı (p < .05 → p , .05), Türkçe ondalık virgülü, sarkan satır sonu tireleri.
 * Bu katman hem arama için normalize edilmiş metni üretir hem de her normalize
 * karakterin orijinal metindeki offset'ini tutar; böylece bir bulgu (finding)
 * orijinal metne ve geometry'ye geri bağlanabilir.
 */

export interface NormalizedText {
	/** Normalize metin (bloklar arası \n\n). */
	text: string;
	/** Her normalize karakterin ham metindeki offset'i (-1: eklendi). */
	map: Int32Array;
	/** Uygulanan dönüşümlerin özeti (güven skoru için). */
	transforms: TextTransform[];
}

export interface TextTransform {
	kind:
		| 'ligature'
		| 'smart-quote'
		| 'dash'
		| 'nbsp'
		| 'soft-hyphen'
		| 'control-char'
		| 'garbage'
		| 'decimal-comma'
		| 'operator-loss'
		| 'hyphen-join'
		| 'whitespace';
	count: number;
}

const LIGATURES: Record<string, string> = {
	'\uFB00': 'ff',
	'\uFB01': 'fi',
	'\uFB02': 'fl',
	'\uFB03': 'ffi',
	'\uFB04': 'ffl',
	'\uFB05': 'st',
	'\uFB06': 'st',
	'\u0132': 'IJ',
	'\u0133': 'ij',
	'\u0152': 'OE',
	'\u0153': 'oe',
	'\u00C6': 'AE',
	'\u00E6': 'ae'
};

const SMART_QUOTES: Record<string, string> = {
	'\u2018': "'",
	'\u2019': "'",
	'\u201A': "'",
	'\u201B': "'",
	'\u2032': "'",
	'\u201C': '"',
	'\u201D': '"',
	'\u201E': '"',
	'\u201F': '"',
	'\u2033': '"',
	'\u00AB': '"',
	'\u00BB': '"',
	'\u2039': "'",
	'\u203A': "'"
};

/** Yalnızca eksi benzeri tireleri dönüştür; en/em dash korunur. */
const DASHES: Record<string, string> = {
	'\u2010': '-',
	'\u2011': '-',
	'\u2012': '-',
	'\u2212': '-'
};

function isControl(ch: string): boolean {
	const c = ch.charCodeAt(0);
	return (c >= 0 && c < 9) || c === 11 || c === 12 || (c >= 14 && c <= 31) || c === 127;
}

/** Cümle/satır içinde geçen "p , .05" gibi operatör kaybı şüphesi. */
export const OPERATOR_LOSS_RE =
	/\b(?:p|t|F|r|z|χ\s*²|χ2|X\s*²|X2)\s*(?:\(\s*[\d,.\s]+\s*\))?\s*[,;]\s*(?:[<>=]\s*)?([.,]?\d+(?:[.,]\d+)?)/g;

interface BuildState {
	out: string[];
	map: number[];
	transforms: Map<TextTransform['kind'], number>;
}

function bump(state: BuildState, kind: TextTransform['kind'], n = 1) {
	state.transforms.set(kind, (state.transforms.get(kind) ?? 0) + n);
}

function push(state: BuildState, ch: string, rawIndex: number) {
	for (const c of ch) {
		state.out.push(c);
		state.map.push(rawIndex);
	}
}

/** Virgülün ondalık ayırıcı olduğu bağlamı kontrol eder. */
function isDecimalCommaAt(raw: string, i: number): boolean {
	if (raw[i] !== ',') return false;
	const prev = raw[i - 1] ?? '';
	const next = raw[i + 1] ?? '';
	if (!/\d/.test(next)) return false;
	// Virgülden sonra en fazla 6 hane + rakam olmayan sınır
	let j = i + 1;
	let digits = 0;
	while (j < raw.length && /\d/.test(raw[j])) {
		digits++;
		j++;
	}
	if (digits === 0 || digits > 6) return false;
	if (/\d/.test(raw[j] ?? '')) return false;
	// Bir önceki karakter rakam olmalı (ya da işaret/boşlukla ayrılmış ".5" durumu)
	if (/\d/.test(prev)) return true;
	if (prev === ' ' || prev === '(' || prev === '=' || prev === '<' || prev === '>' || prev === ',' || prev === '\n')
		return true;
	return false;
}

/**
 * Ham metni normalize eder. `baseOffset` verilirse harita mutlak offset'e çevrilir.
 */
export function normalizeText(raw: string, baseOffset = 0): NormalizedText {
	const state: BuildState = { out: [], map: [], transforms: new Map() };
	let i = 0;
	let lastWasSpace = true;

	while (i < raw.length) {
		const ch = raw[i];

		if (isControl(ch)) {
			bump(state, 'control-char');
			i++;
			continue;
		}

		// Yumuşak tire: tamamen at
		if (ch === '\u00AD') {
			bump(state, 'soft-hyphen');
			i++;
			continue;
		}

		// Ligatürler
		if (LIGATURES[ch]) {
			bump(state, 'ligature');
			push(state, LIGATURES[ch], baseOffset + i);
			i++;
			lastWasSpace = false;
			continue;
		}

		// Akıllı tırnaklar
		if (SMART_QUOTES[ch]) {
			bump(state, 'smart-quote');
			push(state, SMART_QUOTES[ch], baseOffset + i);
			i++;
			lastWasSpace = false;
			continue;
		}

		// Tireler
		if (DASHES[ch]) {
			bump(state, 'dash');
			push(state, DASHES[ch], baseOffset + i);
			i++;
			lastWasSpace = false;
			continue;
		}

		// NBSP ve benzeri boşluklar
		if (ch === '\u00A0' || ch === '\u202F' || ch === '\u2009' || ch === '\u2007') {
			if (!lastWasSpace) {
				bump(state, 'nbsp');
				push(state, ' ', baseOffset + i);
				lastWasSpace = true;
			}
			i++;
			continue;
		}

		// Satır sonu
		if (ch === '\r' || ch === '\n') {
			if (ch === '\r' && raw[i + 1] === '\n') i++;
			// Heceleme ile bölünmüş kelime: "reproduc-\nibility" → "reproducibility"
			const prev = state.out[state.out.length - 1];
			if (prev === '-' && /[a-zà-ÿ]/.test(raw[i + 1] ?? '')) {
				state.out.pop();
				state.map.pop();
				bump(state, 'hyphen-join');
				i++;
				while (i < raw.length && raw[i] === ' ') i++;
				lastWasSpace = false;
				continue;
			}
			push(state, '\n', baseOffset + i);
			lastWasSpace = true;
			i++;
			continue;
		}

		// Boşluk sadeleştirme
		if (ch === ' ' || ch === '\t') {
			if (!lastWasSpace) {
				push(state, ' ', baseOffset + i);
				lastWasSpace = true;
			} else {
				bump(state, 'whitespace');
			}
			i++;
			continue;
		}

		// Türkçe ondalık virgülü: "2,45" → "2.45"
		if (ch === ',' && isDecimalCommaAt(raw, i)) {
			bump(state, 'decimal-comma');
			push(state, '.', baseOffset + i);
			i++;
			lastWasSpace = false;
			continue;
		}

		// Değiştirme karakteri / tanınmayan glif
		if (ch === '\uFFFD' || ch === '\u25A1' || ch === '\u25AF' || ch === '\u25A0') {
			bump(state, 'garbage');
			push(state, ch, baseOffset + i);
			i++;
			lastWasSpace = false;
			continue;
		}

		push(state, ch, baseOffset + i);
		lastWasSpace = false;
		i++;
	}

	const text = state.out.join('');
	const corruptions = countMatches(text, OPERATOR_LOSS_RE);
	if (corruptions > 0) bump(state, 'operator-loss', corruptions);

	return {
		text,
		map: Int32Array.from(state.map),
		transforms: [...state.transforms.entries()].map(([kind, count]) => ({ kind, count }))
	};
}

function countMatches(s: string, re: RegExp): number {
	const local = new RegExp(re.source, re.flags.includes('g') ? re.flags : re.flags + 'g');
	let n = 0;
	while (local.exec(s)) n++;
	return n;
}

/**
 * Türkçe ondalık virgülünü noktaya çevirir (metin düzeyinde; harita gerektirmez).
 * Liste ayırıcısı olan "1, 2, 3" korunur.
 */
export function normalizeDecimalCommas(input: string): string {
	return input.replace(/(?<![\d.,])(-?)(\d{1,3}(?:\.\d{3})*)?,(\d{1,6})(?![\d])/g, (match, sign, intPart) => {
		const int = (intPart ?? '').replace(/\./g, '');
		const frac = match.slice(match.lastIndexOf(',') + 1);
		return `${sign}${int}.${frac}`;
	});
}

/** Operatör kaybı şüphesi. */
export interface OperatorSuspect {
	index: number;
	length: number;
	text: string;
	/** Kurtarılan (tahmini) operatör. */
	recoveredOp?: '<' | '>' | '=';
	/** p değeri okunabildiyse. */
	pValue?: number;
	reason: string;
	confidence: 'high' | 'medium' | 'low';
}

/**
 * "p , .0001" gibi operatör kaybı şüphelerini bulur.
 * Dönen offset'ler normalize metindeki konumlardır.
 */
export function findOperatorSuspects(text: string): OperatorSuspect[] {
	const out: OperatorSuspect[] = [];
	const re = new RegExp(OPERATOR_LOSS_RE.source, 'g');
	let m: RegExpExecArray | null;
	while ((m = re.exec(text))) {
		const raw = m[0];
		const pm = /([.,]?\d+(?:[.,]\d+)?)\s*$/.exec(raw.replace(/\s+/g, ' '));
		let pValue: number | undefined;
		let recoveredOp: OperatorSuspect['recoveredOp'];
		let confidence: OperatorSuspect['confidence'] = 'medium';
		if (pm) {
			const n = Number(pm[1].replace(',', '.'));
			if (Number.isFinite(n) && n >= 0 && n <= 1) {
				pValue = n;
				recoveredOp = n <= 0.05 ? '<' : '=';
				confidence = 'medium';
			}
		}
		out.push({
			index: m.index,
			length: raw.length,
			text: raw,
			recoveredOp,
			pValue,
			reason:
				'İstatistik ifadesinde karşılaştırma operatörünün yerinde virgül/noktalı virgül var; PDF operatör kaybı olabilir.',
			confidence
		});
	}
	return out;
}

/**
 * Normalize metin offset'ini ham metin offset'ine çevirir.
 */
export function toRawOffset(map: Int32Array, index: number): number {
	if (map.length === 0) return index;
	if (index <= 0) return map[0] ?? 0;
	if (index >= map.length) return map[map.length - 1] + 1;
	const v = map[index];
	return v < 0 ? map[Math.max(0, index - 1)] + 1 : v;
}

/** Metni cümlelere böler; her cümlenin başlangıç offset'ini döndürür. */
export function splitSentences(text: string): { text: string; start: number; end: number }[] {
	const out: { text: string; start: number; end: number }[] = [];
	const re = /[^.!?…\n]+(?:[.!?…]+|$)/g;
	let m: RegExpExecArray | null;
	while ((m = re.exec(text))) {
		const s = m[0];
		if (s.trim().length === 0) continue;
		const leading = s.length - s.trimStart().length;
		out.push({ text: s.trim(), start: m.index + leading, end: m.index + s.length });
	}
	return out;
}

export const CONFIDENCE_WEIGHT: Record<'high' | 'medium' | 'low', number> = {
	high: 1,
	medium: 0.65,
	low: 0.3
};

/** Güven skorunu sayısala çevirir. */
export function confidenceWeight(c: 'high' | 'medium' | 'low'): number {
	return CONFIDENCE_WEIGHT[c];
}

const TR_FOLD: Record<string, string> = {
	ç: 'c',
	Ç: 'c',
	ğ: 'g',
	Ğ: 'g',
	ı: 'i',
	İ: 'i',
	I: 'i',
	i: 'i',
	ö: 'o',
	Ö: 'o',
	ş: 's',
	Ş: 's',
	ü: 'u',
	Ü: 'u',
	â: 'a',
	Â: 'a',
	î: 'i',
	Î: 'i',
	û: 'u',
	Û: 'u'
};

/** Arama/karşılaştırma için diakritikleri sadeleştirir (görüntüleme metnini bozmaz). */
export function foldTurkish(s: string): string {
	return s.replace(/[çÇğĞıİIöÖşŞüÜâÂîÎûÛ]/g, (c) => TR_FOLD[c] ?? c);
}

/** Metinden kısa, tek satırlık alıntı üretir. */
export function excerpt(text: string, max = 220): string {
	const clean = text.replace(/\s+/g, ' ').trim();
	return clean.length <= max ? clean : clean.slice(0, max - 1).trimEnd() + '…';
}
